import { Container, Sprite, Texture } from "pixi.js";
import { clamp, lerp } from "../core/math";
import type { WorldBounds } from "../core/WorldBounds";
import type { Divider } from "../entities/Divider";
import type { ForceField } from "../physics/ForceField";

const COUNT = 18;
const SPRING = 7.2;
const DAMP = 4.4;
const WIND = 92000;
const MIN_R = 42;
const MIN_HALF = 14;

interface AuroraBlob {
  halo: Sprite;
  core: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  lane: number;
  along: number;
  phase: number;
  hue: number;
  wobble: number;
}

/**
 * Bolhas-espectro tipo aurora: o vento deforma-as,
 * a mola devolve-as à forma da pontuação.
 */
export class ScoreSpectrum {
  readonly view = new Container();
  private readonly blobs: AuroraBlob[] = [];
  private readonly texture: Texture;
  private shown = 0;

  constructor(private readonly bounds: WorldBounds) {
    this.view.eventMode = "none";
    this.texture = createGlowTexture(256);
    for (let i = 0; i < COUNT; i++) {
      const halo = new Sprite(this.texture);
      const core = new Sprite(this.texture);
      for (const sprite of [halo, core]) {
        sprite.anchor.set(0.5);
        sprite.blendMode = "add";
        sprite.eventMode = "none";
        this.view.addChild(sprite);
      }
      const along = (i + 0.37) / COUNT;
      this.blobs.push({
        halo,
        core,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        lane: Math.sin(i * 1.91 + 0.4),
        along,
        phase: i * 1.13,
        hue: (i * 41 + 120) % 360,
        wobble: 0.55 + (i % 5) * 0.11,
      });
    }
  }

  update(
    dt: number,
    time: number,
    divider: Divider,
    field: ForceField,
    progress: number,
  ): void {
    const target = clamp(progress, 0, 1);
    const tau = target >= 1 ? 0.7 : 2.6;
    const k = 1 - Math.exp(-dt / tau);
    this.shown += (target - this.shown) * k;
    const p = this.shown;
    const w = this.bounds.width;
    const h = this.bounds.height;
    const maxR = Math.max(h, w) * 0.48;
    const restR = lerp(MIN_R, maxR, p);

    for (const blob of this.blobs) {
      const rest = divider.pointAlong(blob.along);
      const restX = rest.x;
      const lineY = rest.y;
      const cover = divider.isOval
        ? Math.min(divider.rx, divider.ry) * 0.92
        : Math.max(lineY, h - lineY) + 8;
      const half = p >= 1 ? cover : lerp(MIN_HALF, cover, p);
      const restY = divider.isOval
        ? lineY + blob.lane * half * (blob.lane >= 0 ? -0.35 : 0.35)
        : lineY + blob.lane * half * 0.7;
      const maxOff = restR * (0.22 + p * 0.2);

      if (blob.x === 0 && blob.y === 0) {
        blob.x = restX;
        blob.y = restY;
      }

      const wind = field.sample(blob.x || restX, blob.y || restY, time, divider);
      const ax =
        (restX - blob.x) * SPRING -
        blob.vx * DAMP +
        clamp(wind.x * WIND, -420, 420);
      const ay =
        (restY - blob.y) * SPRING -
        blob.vy * DAMP +
        clamp(wind.y * WIND, -420, 420);
      blob.vx += ax * dt;
      blob.vy += ay * dt;
      blob.x = restX + clamp(blob.x + blob.vx * dt - restX, -maxOff, maxOff);
      blob.y = restY + clamp(blob.y + blob.vy * dt - restY, -maxOff, maxOff);

      const breathe = 0.5 + 0.5 * Math.sin(time * (0.55 + blob.wobble * 0.25) + blob.phase);
      const fade = 0.5 + 0.5 * Math.sin(time * 0.33 + blob.phase * 1.4);
      const hue = blob.hue + time * 18 + blob.lane * 40;
      const color = hslToHex(hue, 0.88, 0.6);
      const coreColor = hslToHex(hue + 28, 0.95, 0.68);
      const alpha = (0.16 + 0.2 * p) * (0.45 + 0.55 * breathe * fade);
      const stretch = 1 + clamp(wind.x * 18000, -0.22, 0.22);
      const tall = 1 + clamp(wind.y * 18000, -0.22, 0.22);
      const size = restR * (0.78 + 0.22 * breathe);
      const rot = clamp(wind.x * 8000, -0.35, 0.35) * 0.4;

      blob.halo.tint = color;
      blob.halo.alpha = alpha * 0.7;
      blob.halo.position.set(blob.x, blob.y);
      blob.halo.width = size * (1.35 + p * 0.4) * stretch;
      blob.halo.height = size * (0.95 + p * 0.25) * tall;
      blob.halo.rotation = rot;

      blob.core.tint = coreColor;
      blob.core.alpha = Math.min(0.55, alpha * 1.15);
      blob.core.position.set(blob.x, blob.y);
      blob.core.width = size * 0.48 * stretch;
      blob.core.height = size * 0.4 * tall;
      blob.core.rotation = rot;
    }
  }
}

function createGlowTexture(size: number): Texture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(size, size);
  const cx = (size - 1) * 0.5;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - cx) / cx;
      const dy = (y - cx) / cx;
      const d2 = dx * dx + dy * dy;
      const v = d2 >= 1 ? 0 : (1 - d2) * (1 - d2);
      const i = (y * size + x) * 4;
      img.data[i] = 255;
      img.data[i + 1] = 255;
      img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(v * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  const texture = Texture.from(canvas);
  texture.source.scaleMode = "linear";
  return texture;
}

function hslToHex(h: number, s: number, l: number): number {
  const hue = (((h % 360) + 360) % 360) / 360;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + hue * 12) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(clamp(v, 0, 1) * 255);
  };
  return (f(0) << 16) | (f(8) << 8) | f(4);
}
